import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material'
import {
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material'
import { generateVerificationCode, getMSPStatus, verifyMyDNS } from '../services/mspService'

interface GenerateVerificationCodeProps {
  tenantId: string
  onCodeGenerated?: () => void
}

export default function GenerateVerificationCode({ tenantId, onCodeGenerated }: GenerateVerificationCodeProps) {
  const [verificationCode, setVerificationCode] = useState<string | null>(null)
  const [relationshipStatus, setRelationshipStatus] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [verifyingDNS, setVerifyingDNS] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailWarning, setEmailWarning] = useState<string | null>(null)
  const [dnsVerifyMessage, setDnsVerifyMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  // MSP identity the customer is authorizing — collected before code generation
  const [mspEmailDomain, setMspEmailDomain] = useState('')
  const [mspDomainError, setMspDomainError] = useState<string | null>(null)

  // Load MSP status on mount
  useEffect(() => {
    loadMSPStatus()
  }, [tenantId])

  const loadMSPStatus = async () => {
    try {
      const status = await getMSPStatus(tenantId)
      if (status.hasVerificationCode) {
        setVerificationCode(status.verificationCode)
        setRelationshipStatus(status.relationshipStatus)
        
        // Set active step based on status
        if (status.relationshipStatus === 'DNSVerified' || status.relationshipStatus === 'Accepted' || status.relationshipStatus === 'Verified') {
          setActiveStep(2) // MSP Verification step
        } else if (status.relationshipStatus === 'waiting') {
          setActiveStep(1) // DNS setup step
        } else {
          setActiveStep(0) // Generate code step
        }
      }
    } catch (err) {
      console.error('Failed to load MSP status:', err)
    }
  }

  const handleGenerateCode = async () => {
    // Validate MSP identity before requesting the code
    const domain = mspEmailDomain.trim().toLowerCase().replace(/^@/, '')
    if (!domain || !domain.includes('.')) {
      setMspDomainError('Enter the email domain of your MSP partner (e.g. contosomsp.com).')
      return
    }
    setMspDomainError(null)

    try {
      setLoading(true)
      setError(null)
      setEmailWarning(null)
      const data = await generateVerificationCode(tenantId, { mspEmailDomain: domain })
      if (!data.verificationCode) {
        throw new Error('Verification code was not returned by the service. Please retry.')
      }
      setVerificationCode(data.verificationCode)
      setRelationshipStatus('waiting')
      setActiveStep(1) // Auto-advance to DNS setup

      if (data.emailSendStatus === 'Failed') {
        setEmailWarning(`Verification code generated, but email delivery failed: ${data.emailError || 'Unknown email error'}`)
      }

      if (onCodeGenerated) {
        onCodeGenerated()
      }
    } catch (err: any) {
      console.error('Failed to generate verification code:', err)
      const serverMsg: string | undefined =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message
      setError(serverMsg || 'Failed to generate verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyDNS = async () => {
    try {
      setVerifyingDNS(true)
      setError(null)
      setEmailWarning(null)
      setDnsVerifyMessage(null)
      const result = await verifyMyDNS(tenantId)
      
      if (result.verified) {
        setRelationshipStatus('DNSVerified')
        setDnsVerifyMessage(result.message)
        if (result.emailSendStatus === 'Failed') {
          setEmailWarning(`DNS verified, but MSP instructions email failed: ${result.emailError || 'Unknown email error'}`)
        }
        setActiveStep(2) // Move to MSP verification step
        if (onCodeGenerated) {
          onCodeGenerated()
        }
      } else {
        setDnsVerifyMessage(result.message)
      }
    } catch (err: any) {
      console.error('Failed to verify DNS:', err)
      setError(err.response?.data?.error || 'Failed to verify DNS record')
    } finally {
      setVerifyingDNS(false)
    }
  }

  const handleCopyCode = () => {
    if (verificationCode) {
      navigator.clipboard.writeText(verificationCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCopyDNSRecord = () => {
    if (verificationCode) {
      const dnsRecord = `tc-config-verify=${verificationCode}`
      navigator.clipboard.writeText(dnsRecord)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const isDnsVerified =
    relationshipStatus === 'DNSVerified' ||
    relationshipStatus === 'Accepted' ||
    relationshipStatus === 'Verified'

  const steps = [
    {
      label: 'Generate Verification Code',
      content: (
        <Box>
          <Typography variant="body2" paragraph>
            Generate a unique verification code that your MSP partner will use to verify access to your tenant.
            You must identify your MSP partner below so that only they can complete the verification.
          </Typography>

          {!verificationCode && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
              <TextField
                label="MSP email domain"
                placeholder="e.g. contosomsp.com"
                value={mspEmailDomain}
                onChange={(e) => {
                  setMspEmailDomain(e.target.value)
                  setMspDomainError(null)
                }}
                error={!!mspDomainError}
                helperText={
                  mspDomainError ??
                  'Enter the domain portion of your MSP partner\'s email address. This ensures only your MSP can claim this tenant.'
                }
                size="small"
                fullWidth
                disabled={loading}
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <KeyIcon />}
                onClick={handleGenerateCode}
                disabled={loading || !mspEmailDomain.trim()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {loading ? 'Generating...' : 'Generate Code'}
              </Button>
            </Box>
          )}

          {verificationCode && (
            <Alert severity="success">
              <Typography variant="body2">
                ✓ Verification code generated successfully! See the green box above for your code.
              </Typography>
            </Alert>
          )}
        </Box>
      )
    },
    {
      label: 'Add DNS TXT Record',
      content: verificationCode ? (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Your verification code is displayed in the green box above. Use it to create the DNS TXT record below.
            </Typography>
          </Alert>

          <Typography variant="subtitle2" gutterBottom>
            Add this DNS TXT record to your domain:
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary">
                Type:
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                TXT
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary">
                Name/Host:
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                @ (root domain) or subdomain
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="caption" color="text.secondary">
                Value/Content:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  value={`tc-config-verify=${verificationCode}`}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                  }}
                />
                <Tooltip title={copied ? 'Copied!' : 'Copy DNS record'}>
                  <IconButton onClick={handleCopyDNSRecord} color={copied ? 'success' : 'primary'}>
                    {copied ? <CheckCircleIcon /> : <CopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                TTL:
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                3600 (1 hour) or default
              </Typography>
            </Box>
          </Paper>

          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Next Steps:</strong>
            </Typography>
            <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Log in to your DNS provider (GoDaddy, Cloudflare, Azure DNS, etc.)</li>
              <li>Add the TXT record above to your domain</li>
              <li>Click "Verify DNS Record" below to confirm the record is active</li>
              <li>You'll receive an email with instructions to forward to your MSP partner</li>
            </ol>
          </Alert>

          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderColor: isDnsVerified ? 'success.main' : 'warning.main',
              bgcolor: isDnsVerified ? 'success.50' : '#fff59d'
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Verify DNS Record
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              {isDnsVerified
                ? 'DNS verification is complete. You can continue to the next step.'
                : 'Verification pending. Click the button below after your TXT record has propagated.'}
            </Typography>

            <Button
              variant="contained"
              color={isDnsVerified ? 'success' : 'warning'}
              startIcon={verifyingDNS ? <CircularProgress size={20} color="inherit" /> : <DoneAllIcon />}
              onClick={handleVerifyDNS}
              disabled={verifyingDNS || isDnsVerified}
              sx={!isDnsVerified ? { color: 'warning.contrastText' } : undefined}
            >
              {verifyingDNS ? 'Verifying...' : isDnsVerified ? 'DNS Record Verified' : 'Verify DNS Record'}
            </Button>

            {isDnsVerified && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  ✓ DNS record verified! Check your email for instructions to forward to your MSP partner.
                </Typography>
              </Alert>
            )}

            {dnsVerifyMessage && (
              <Alert severity={isDnsVerified ? 'success' : 'warning'} sx={{ mt: 2 }}>
                {dnsVerifyMessage}
              </Alert>
            )}
          </Paper>
        </Box>
      ) : null
    },
    {
      label: 'MSP Verification',
      content: (
        <Box>
          {relationshipStatus === 'DNSVerified' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ✓ Your DNS record has been verified! You should have received an email with instructions.
                </Typography>
              </Alert>
              <Typography variant="body2" paragraph>
                <strong>Next Steps:</strong>
              </Typography>
              <ol style={{ marginTop: 0, paddingLeft: 20 }}>
                <li>Check your email inbox for the MSP instructions email</li>
                <li>Forward the email to your MSP partner</li>
                <li>Your MSP will use the verification code to complete the setup in their portal</li>
              </ol>
            </>
          )}

          {(relationshipStatus === 'Accepted' || relationshipStatus === 'Verified') && (
            <Alert severity="success">
              <Typography variant="body2">
                ✓ MSP relationship established! Your MSP partner now has access to your TC-Config portal.
              </Typography>
            </Alert>
          )}

          {(!relationshipStatus || relationshipStatus === 'waiting') && (
            <>
              <Typography variant="body2" paragraph>
                After verifying your DNS record, your MSP partner will:
              </Typography>
              <ul style={{ marginTop: 0, paddingLeft: 20 }}>
                <li>Receive instructions via email (forwarded by you)</li>
                <li>Verify the DNS TXT record in their MSP portal</li>
                <li>Establish the relationship and gain read-only access</li>
                <li>View your deployments and policies</li>
              </ul>
            </>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Once verified, the MSP relationship will appear in the "Active MSP Relationships" section below.
            </Typography>
          </Alert>
        </Box>
      )
    }
  ]

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <KeyIcon color="primary" />
        <Typography variant="h6">Grant MSP Access</Typography>
      </Box>

      {verificationCode && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Box flex={1}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Your Verification Code:
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  value={verificationCode}
                  fullWidth
                  size="small"
                  InputProps={{
                    readOnly: true,
                    sx: { 
                      fontFamily: 'monospace', 
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      bgcolor: 'white'
                    }
                  }}
                />
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                  <IconButton onClick={handleCopyCode} color={copied ? 'success' : 'primary'} size="small">
                    {copied ? <CheckCircleIcon /> : <CopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Share this code with your MSP partner. They will use it to verify your domain and establish access.
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {emailWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {emailWarning}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel>{step.label}</StepLabel>
            <StepContent>
              {step.content}
              {index < steps.length - 1 && verificationCode && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(index + 1)}
                    size="small"
                    disabled={index === 1 && !isDnsVerified}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Paper>
  )
}
